<template>
  <div class="flex flex-row gap-0 min-h-[420px] h-[min(70vh,calc(100vh-220px))] w-full min-w-0">
    <div class="relative flex flex-1 min-w-0 min-h-0 flex-col border-r border-primary/15 pl-3 pr-3">
      <div
        v-if="!isListPending && !isListError && allItems.length > 0"
        class="shrink-0 pb-3 pt-1 pl-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
      >
        <label class="sr-only" for="activity-log-search">Search activity logs</label>
        <input
          id="activity-log-search"
          v-model="searchText"
          type="search"
          placeholder="Search logs…"
          class="min-w-0 flex-1 rounded-lg border-2 border-primary/20 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <label class="sr-only" for="activity-log-kind">Filter by activity type</label>
        <select
          id="activity-log-kind"
          v-model="selectedKindFilter"
          class="w-full shrink-0 rounded-lg border-2 border-primary/20 bg-white px-3 py-2 text-sm font-medium text-primary-dark shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-[min(220px,100%)]"
        >
          <option value="">All types</option>
          <option v-for="opt in ORDER_ACTIVITY_KIND_OPTIONS" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
      </div>

      <div class="relative min-h-0 flex-1 overflow-y-auto">
        <div class="pb-2">
          <div v-if="isListPending" class="flex flex-col items-center justify-center py-12 text-gray-700">
            <svg class="animate-spin h-8 w-8 text-primary mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            <p class="text-sm font-semibold text-primary-dark">Loading activity logs…</p>
          </div>

          <div v-else-if="isListError" class="py-10 text-center text-sm font-medium text-red-600">
            Something went wrong loading activity logs.
          </div>

          <div
            v-else-if="!allItems.length"
            class="flex flex-col items-center justify-center py-10 border-2 border-dashed border-primary/25 rounded-xl bg-primary-lighter/50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mb-3 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 17v-2m3 2v-4m3 4v-6M4 4h16v16H4V4z" />
            </svg>
            <p class="text-base font-bold text-primary-dark">No activity yet</p>
            <p class="text-sm text-gray-700 mt-1.5 max-w-xs text-center">Actions on this order will appear here.</p>
          </div>

          <div
            v-else-if="!filteredItems.length"
            class="flex flex-col items-center justify-center py-10 border-2 border-dashed border-amber-200 rounded-xl bg-amber-50/80"
          >
            <p class="text-base font-bold text-primary-dark">No logs match your filters</p>
            <p class="text-sm text-gray-700 mt-1.5 max-w-xs text-center">Try different search text or activity type.</p>
            <button
              type="button"
              class="mt-4 rounded-lg border-2 border-primary bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-dark"
              @click="clearFilters"
            >
              Clear filters
            </button>
          </div>

          <TimeLine
            v-else
            :is-order-status="false"
            :direction="'vertical'"
            :alternating="false"
            :total-items="filteredItems.length"
            :current-step="-1"
          >
            <TimeLineItem
              v-for="(log, idx) in filteredItems"
              :key="log.id"
              class="activity-log-timeline-item cursor-pointer rounded-lg transition-colors duration-200 ease-out"
              :class="
                selectedLogId === log.id
                  ? 'bg-primary/30 shadow-md'
                  : 'hover:bg-gray-50/90'
              "
              :title="log.title"
              attribution=""
              :index="idx"
              :total-items="filteredItems.length"
              :current-step="-1"
              :alternating="false"
              activity-date-time=""
              :icon="getActivityLogListIcon(log)"
              :completed-icon="CheckCircleIcon"
              :action-log="undefined"
              :activity-user="undefined"
              :show-important-banner="false"
              @click="toggleLog(log)"
            >
              <div class="activity-log-card flex gap-4 min-h-[4rem] text-left">
                <div class="min-w-0 flex-1 flex flex-col gap-2.5 pr-1">
                  <p class="text-sm leading-relaxed text-gray-800">
                    {{ log.description }}
                  </p>
                  <div
                    v-if="log.user?.name || log.user?.role"
                    class="mt-1 border-t border-gray-200/80 pt-2.5"
                  >
                    <p
                      v-if="log.user?.name"
                      class="text-sm font-semibold leading-tight text-gray-900"
                    >
                      {{ log.user.name }}
                    </p>
                    <p
                      v-if="log.user?.role"
                      class="mt-0.5 text-[11px] font-medium leading-snug text-gray-500"
                    >
                      {{ log.user.role }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex w-[6.75rem] shrink-0 flex-col items-end border-l border-gray-200/90 pl-3 text-right sm:w-36"
                  aria-label="When"
                >
                  <time
                    class="text-[11px] font-medium tabular-nums leading-snug text-gray-500 sm:text-xs"
                    :datetime="log.occurred_at"
                  >
                    {{ formatWhen(log.occurred_at) }}
                  </time>
                </div>
              </div>
            </TimeLineItem>
          </TimeLine>
        </div>
      </div>
    </div>

    <Transition name="activity-detail-slide">
      <div
        v-if="selectedLogId"
        class="w-[42%] min-w-[280px] max-w-xl shrink-0 pl-3 flex flex-col min-h-0"
      >
        <OrderActivityLogDetailPane
          :detail="detailForPane"
          :is-loading="detailLoading"
          :is-error="detailError"
          @close="selectedLogId = null"
        />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { useOrderActivityLogDetail, useOrderActivityLogs } from '@/composables/useOrderActivityLogs';
import { getActivityLogListIcon } from '@/composables/useOrderActivityLogTimelineIcons';
import {
  ORDER_ACTIVITY_KIND_OPTIONS,
  type OrderActivityKind,
  type OrderActivityLogDetail,
  type OrderActivityLogListItem,
} from '@/models/OrderActivityLog';
import { CheckCircleIcon } from '@heroicons/vue/24/outline';
import { computed, ref, watch } from 'vue';
import TimeLine from './TimeLine.vue';
import TimeLineItem from './TimeLineItem.vue';
import OrderActivityLogDetailPane from './OrderActivityLogDetailPane.vue';

const props = defineProps<{
  /** Parent order id (kept for display / future use). */
  orderId: number;
  /** Order item id - drives the activity-log API endpoints. */
  orderItemId: number;
  /** Used as a remount / reset key when the user switches the active row. */
  rowKey: number;
}>();

const selectedLogId = ref<string | null>(null);
const searchText = ref('');
/** Empty string = all kinds */
const selectedKindFilter = ref<OrderActivityKind | ''>('');

const { listQuery } = useOrderActivityLogs(() => props.orderItemId);
const { detailQuery } = useOrderActivityLogDetail(
  () => props.orderItemId,
  () => selectedLogId.value,
);

const isListPending = computed(() => listQuery.isPending.value);
const isListError = computed(() => listQuery.isError.value);

const allItems = computed(() => listQuery.data.value ?? []);

const filteredItems = computed(() => {
  let rows = allItems.value;
  if (selectedKindFilter.value) {
    rows = rows.filter((log) => log.activity_kind === selectedKindFilter.value);
  }
  const q = searchText.value.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((log) => logMatchesQuery(log, q));
});

const detailForPane = computed<OrderActivityLogDetail | null>(() => detailQuery.data.value ?? null);
const detailLoading = computed(() => detailQuery.isPending.value);
const detailError = computed(() => detailQuery.isError.value);

watch(
  () => [props.orderId, props.orderItemId, props.rowKey] as const,
  () => {
    selectedLogId.value = null;
    searchText.value = '';
    selectedKindFilter.value = '';
  },
);

watch(filteredItems, (rows) => {
  if (selectedLogId.value && !rows.some((l) => l.id === selectedLogId.value)) {
    selectedLogId.value = null;
  }
});

function logMatchesQuery(log: OrderActivityLogListItem, q: string): boolean {
  const whenFormatted = formatWhen(log.occurred_at);
  const parts = [
    log.title,
    log.description,
    log.user?.name ?? '',
    log.user?.role ?? '',
    log.activity_type ?? '',
    log.activity_kind ?? '',
    log.occurred_at,
    whenFormatted,
  ];
  return parts.some((s) => s.toLowerCase().includes(q));
}

function clearFilters() {
  searchText.value = '';
  selectedKindFilter.value = '';
}

function toggleLog(log: OrderActivityLogListItem) {
  selectedLogId.value = selectedLogId.value === log.id ? null : log.id;
}

/** e.g. Jan 28 2026, 14:32 (local) */
function formatWhen(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const pad = (n: number) => String(n).padStart(2, '0');
  const datePart = `${months[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`;
  const timePart = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${datePart}, ${timePart}`;
}
</script>

<style scoped>
/* Tighter gap between title row and description pane for activity-log layout */
:deep(.timeline-item-header) {
  margin-bottom: 0.375rem;
}

.activity-detail-slide-enter-active,
.activity-detail-slide-leave-active {
  transition:
    transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
}
.activity-detail-slide-enter-from,
.activity-detail-slide-leave-to {
  transform: translateX(12px);
  opacity: 0;
}
</style>
